import React from 'react';
import DictSelect from '../../../components/DictSelect';
import I18nEditor from '../../../components/I18nEditor';
import ImageUploader from '../../../components/ImageUploader';
import {
    CategoryCascade,
    RollRatioInput,
    TraitMultiSelect,
    useCategoryTree,
    useMaterialTraits,
} from '../../../components/MaterialFieldInputs';
import { FieldIssue } from '../../../components/wizard/WizardShell';
import { MaterialBundlePayload } from '../../../api/bundle';
import { nextMaterialXcode } from '../../../api/material';
import { I18n } from '../../../utils/i18n';
import { IssueByPath } from './useBundleDraft';
import SearchSelect from '../../../components/SearchSelect';
import { FLEXIBILITY_EMPTY_LABEL, FLEXIBILITY_SELECT_OPTS } from '../selectOptions';

// 向导第 ① 步: 材料主数据 (唯一必填步).
// 表单字段与原「新建材料」弹窗一致 —— 那个弹窗随本向导上线后下线, 这里是它的唯一去处.
// 差别只在提交方式: 不再逐个接口建, 而是填进套件载荷, 和后面几步一起走一次审批.

interface Props {
    payload: MaterialBundlePayload;
    patch: (p: Partial<MaterialBundlePayload['material']>) => void;
    issueByPath: IssueByPath;
    /** 有 material:publish 权限 (决定 ⑤ 步发布开关可不可用, 这里只做提示) */
    canPublish: boolean;
}

function StepBasic({ payload, patch, issueByPath }: Props) {
    const categoryTree = useCategoryTree();
    const materialTraits = useMaterialTraits();
    const m = payload.material;
    // xcodeAuto: 编码是否还处于"自动生成"状态 —— 用户手改过就不再覆盖 (口径同原弹窗材-02)
    const [xcodeAuto, setXcodeAuto] = React.useState(true);

    const name: I18n = {
        zh: m.nameZh || '',
        en: m.nameEn || '',
        ...(m.name || {}),
    };

    const onName = (next: I18n) => {
        const { zh, en, ...rest } = next;
        patch({
            nameZh: zh || '',
            nameEn: en || '',
            name: Object.keys(rest).length ? (rest as Record<string, string>) : undefined,
        });
    };

    const onCategory = (t1: string, t2: string) => {
        patch({ type1: t1, type2: t2 });
        // 选定二级类别即预取编码: 让用户在第一步就看到这个材料将来的编号,
        // 真正占号要等落库事务 (dryRun 不占号, 并发也不会撞).
        if (t2 && (xcodeAuto || !m.xcode)) {
            nextMaterialXcode(t1, t2)
                .then((r) => {
                    if (r.xcode) {
                        patch({ xcode: r.xcode });
                        setXcodeAuto(true);
                    }
                })
                .catch(() => undefined);
        }
    };

    return (
        <>
            <div className="card p-4 space-y-3">
                <div className="text-sm font-semibold">类别与编码</div>
                <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                        <label className="field-label">
                            <span className="text-rose-500">* </span>
                            类别 (一级 / 二级, 来源于材料类别管理)
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                            <CategoryCascade
                                tree={categoryTree}
                                type1={m.type1}
                                type2={m.type2}
                                onChange={onCategory}
                            />
                        </div>
                        <FieldIssue issue={issueByPath['material.type1']} />
                        <FieldIssue issue={issueByPath['material.type2']} />
                    </div>
                    <div className="col-span-2">
                        <label className="field-label">材质编码 (xcode · 业务主键)</label>
                        <input
                            className="inp font-mono"
                            value={m.xcode || ''}
                            onChange={(e) => {
                                patch({ xcode: e.target.value });
                                setXcodeAuto(false);
                            }}
                            placeholder="留空 = 落库时按类别前缀自动取号"
                        />
                        {xcodeAuto && m.xcode && (
                            <div className="text-[11px] text-emerald-600 mt-1">
                                已按类别前缀预取, 可手工修正; 留空则落库时自动取号
                            </div>
                        )}
                        <FieldIssue issue={issueByPath['material.xcode']} />
                    </div>
                </div>
            </div>

            <div className="card p-4 space-y-3">
                <div className="text-sm font-semibold">名称与主图</div>
                <div>
                    <label className="field-label">
                        <span className="text-rose-500">* </span>
                        名称 (多语言, 中文必填)
                    </label>
                    <I18nEditor value={name} onChange={onName} />
                    <FieldIssue issue={issueByPath['material']} />
                    <FieldIssue issue={issueByPath['material.nameZh']} />
                </div>
                <div>
                    <label className="field-label">主图</label>
                    <ImageUploader
                        value={m.image || ''}
                        onChange={(url) => patch({ image: url })}
                        size="md"
                    />
                    <div className="text-[11px] text-slate-400 mt-1">
                        支持 JPG / PNG / WebP / GIF, 最大 20 MiB
                    </div>
                </div>
            </div>

            <div className="card p-4 space-y-3">
                <div className="text-sm font-semibold">属性</div>
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="field-label">工艺 1</label>
                        <DictSelect
                            category="craft"
                            className="sel w-full"
                            value={m.craft1 || ''}
                            onChange={(v) => patch({ craft1: v })}
                        />
                        <FieldIssue issue={issueByPath['material.craft1']} />
                    </div>
                    <div>
                        <label className="field-label">工艺 2</label>
                        <DictSelect
                            category="craft"
                            className="sel w-full"
                            value={m.craft2 || ''}
                            onChange={(v) => patch({ craft2: v })}
                        />
                        <FieldIssue issue={issueByPath['material.craft2']} />
                    </div>
                    <div>
                        <label className="field-label">表面处理 1</label>
                        <DictSelect
                            category="surface"
                            className="sel w-full"
                            value={m.surface1 || ''}
                            onChange={(v) => patch({ surface1: v })}
                        />
                        <FieldIssue issue={issueByPath['material.surface1']} />
                    </div>
                    <div>
                        <label className="field-label">表面处理 2</label>
                        <DictSelect
                            category="surface"
                            className="sel w-full"
                            value={m.surface2 || ''}
                            onChange={(v) => patch({ surface2: v })}
                        />
                        <FieldIssue issue={issueByPath['material.surface2']} />
                    </div>
                    <div>
                        <label className="field-label">颜色</label>
                        <DictSelect
                            category="color"
                            className="sel w-full"
                            value={m.color || ''}
                            onChange={(v) => patch({ color: v })}
                        />
                        <FieldIssue issue={issueByPath['material.color']} />
                    </div>
                    <div>
                        <label className="field-label">颜色 hex</label>
                        <div className="flex items-center gap-2">
                            <input
                                type="color"
                                value={m.colorHex || '#000000'}
                                onChange={(e) => patch({ colorHex: e.target.value })}
                                className="h-8 w-12 border border-slate-200 rounded cursor-pointer"
                            />
                            <input
                                className="inp font-mono flex-1"
                                value={m.colorHex || ''}
                                onChange={(e) => patch({ colorHex: e.target.value })}
                                placeholder="#0A0A0A"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="field-label">加工目标</label>
                        <input
                            className="inp"
                            value={m.target || ''}
                            onChange={(e) => patch({ target: e.target.value })}
                            placeholder="去涂层 / 切割 / 雕刻"
                        />
                    </div>
                    <div>
                        <label className="field-label">涂层厚度 (mm)</label>
                        <input
                            className="inp"
                            type="number"
                            step="0.001"
                            value={m.coatingMm ?? ''}
                            onChange={(e) => {
                                const v = Number(e.target.value);
                                patch({
                                    coatingMm: e.target.value === '' || isNaN(v) ? undefined : v,
                                });
                            }}
                            placeholder="0.08"
                        />
                    </div>
                    <div>
                        <label className="field-label">光泽度</label>
                        <input
                            className="inp"
                            value={m.gloss || ''}
                            onChange={(e) => patch({ gloss: e.target.value })}
                            placeholder="高 / 中 / 低 / —"
                        />
                    </div>
                    <div>
                        <label className="field-label">卷料传送比</label>
                        <RollRatioInput
                            className="inp"
                            value={m.axisYTransmissionRatioRollMaterial}
                            onChange={(v) =>
                                patch({ axisYTransmissionRatioRollMaterial: v || undefined })
                            }
                        />
                        <FieldIssue
                            issue={issueByPath['material.axisYTransmissionRatioRollMaterial']}
                        />
                    </div>
                    <div>
                        <label className="field-label">材料柔韧性</label>
                        <SearchSelect
                            className="w-full"
                            value={m.flexibility || ''}
                            onChange={(v) => patch({ flexibility: v })}
                            placeholder={FLEXIBILITY_EMPTY_LABEL}
                            options={FLEXIBILITY_SELECT_OPTS}
                            clearable
                        />
                        <FieldIssue issue={issueByPath['material.flexibility']} />
                    </div>
                    <div className="col-span-2">
                        <label className="field-label">特性备注 (自由文本)</label>
                        <input
                            className="inp"
                            value={m.traits || ''}
                            onChange={(e) => patch({ traits: e.target.value })}
                        />
                    </div>
                    <div className="col-span-2">
                        <label className="field-label">材料特性 (多选)</label>
                        <TraitMultiSelect
                            traits={materialTraits}
                            value={m.characteristics || []}
                            onChange={(codes) => patch({ characteristics: codes })}
                        />
                    </div>
                    <div className="col-span-2">
                        <label className="field-label">备注</label>
                        <input
                            className="inp"
                            value={m.remark || ''}
                            onChange={(e) => patch({ remark: e.target.value })}
                            placeholder="可选"
                        />
                    </div>
                </div>
            </div>
        </>
    );
}

export default StepBasic;
